import * as React from "react"
import { natmap_outputs } from "../projects/modelling/components/natmap_soil_component"
import { tileGridStats } from "../projects/modelling/tile_grid"
import {
  ATILayerSettings,
  CehLandCoverLayerSettings,
  CropMapLayerSettings,
  GeoserverLayerLegend,
  GeoserverLayerSettings,
  GenericLegend,
  IMDLayerSettings,
  KewLayerSettings,
  KewPointLayerSettings,
  KewShapeLayerSettings,
  LayerSettingsHeader,
  ModelOutputLayerLegend,
  ModelOutputLayerSettings,
  NevoLayerSettings,
  OverlayLayerSettings,
  PaintbrushGeoJSONLegend,
  ShapeLayerLegend,
  ShapeLayerSettings,
  WFSLayerSettings,
  layerLegendStyleLabel,
  collapsedLegendValueLabels,
} from "../projects/editor/sidebar"
import {
  CopyLayerStyleControl,
  isModelDatasetLayer,
  isTileGridLoaded,
  type ModelDatasetStyleCopyEntry,
} from "../projects/layers/layer_style"
import { DatasetLayer, Layer, ModelOutputLayer } from "../projects/state"
import {
  labelEntriesFromStatsLabels,
  mergeCategoricalStyleOnCopy,
} from "../projects/layers/categorical_layer_colors"

/** Legend + layer settings block shared with project sidebar behaviour (no export / save UI). */
export type PaintbrushLayerInspectorProps = {
  selectedLayer: Layer | null
  mutate: (data: Record<string, unknown>) => void
  getLayerData: (layer: DatasetLayer | ModelOutputLayer) => tileGridStats
  isLegCollapsed: boolean
  onToggleLegend: () => void
  /** When true, layer title is shown as plain text (e.g. current annotation on paintbrush). */
  lockLayerName?: boolean
  modelDatasetStyleEntries?: ModelDatasetStyleCopyEntry[]
  styleCopyTargetId?: string | number
  onLoadInClasses?: () => void
}

export const PaintbrushLayerInspector: React.FC<PaintbrushLayerInspectorProps> = ({
  selectedLayer,
  mutate,
  getLayerData,
  isLegCollapsed,
  onToggleLegend,
  lockLayerName = false,
  modelDatasetStyleEntries = [],
  styleCopyTargetId,
  onLoadInClasses
}) => {
  const [settingsCollapsed, setSettingsCollapsed] = React.useState(false)
  const layerStats =
    selectedLayer !== null &&
    (selectedLayer.type === "DatasetLayer" || selectedLayer.type === "ModelOutputLayer")
      ? getLayerData(selectedLayer)
      : null
  const showLoadInClassesButton =
    selectedLayer?.type === "DatasetLayer" &&
    layerStats?.type === "CategoricalTileGrid" &&
    isTileGridLoaded(layerStats) &&
    onLoadInClasses !== undefined
  const showLegendChrome =
    selectedLayer?.type === "ModelOutputLayer" ||
    selectedLayer?.type === "DatasetLayer" ||
    selectedLayer?.type === "IMDLayer" ||
    selectedLayer?.type === "KewPointLayer" ||
    selectedLayer?.type === "WFSLayer" ||
    selectedLayer?.type === "ShapeLayer" ||
    (selectedLayer?.type === "GeoserverLayer" && !!selectedLayer.fill) ||
    (selectedLayer?.type === "KewShapeLayer" && selectedLayer.metric.name !== "none") ||
    selectedLayer?.type === "PaintbrushGeoJSONLayer"

  return (
    <>
      {showLegendChrome && (
        <LayerSettingsHeader
          title="Layer legend"
          keepTitle
          collapsed={isLegCollapsed}
          onToggle={onToggleLegend}
          typeLabel={layerLegendStyleLabel(
            selectedLayer,
            selectedLayer && isModelDatasetLayer(selectedLayer) ? getLayerData(selectedLayer).type : undefined
          )}
          valueLabels={collapsedLegendValueLabels(
            selectedLayer,
            selectedLayer && isModelDatasetLayer(selectedLayer) ? getLayerData : undefined
          )}
        />
      )}
      {(selectedLayer?.type === "ModelOutputLayer" || selectedLayer?.type === "DatasetLayer") && (
        <ModelOutputLayerLegend
          layer={selectedLayer}
          getLayerData={getLayerData}
          mutateColors={styleUpdate => mutate(styleUpdate as Record<string, unknown>)}
          updateBounds={(overrideBounds, bounds) => mutate({ overrideBounds, bounds })}
          toggle={isLegCollapsed}
        />
      )}
      {selectedLayer?.type === "ShapeLayer" && (
        <ShapeLayerLegend layer={selectedLayer} mutate={data => mutate(data)} toggle={isLegCollapsed} />
      )}
      {selectedLayer?.type === "GeoserverLayer" && selectedLayer.fill && (
        <GeoserverLayerLegend layer={selectedLayer} mutate={data => mutate(data)} toggle={isLegCollapsed} />
      )}
      {selectedLayer?.type === "IMDLayer" && (
        <GenericLegend
          min={`Least (${selectedLayer.property.min}${selectedLayer.property.min === 10 ? "th Decile" : ""})`}
          max={`Most (${selectedLayer.property.max}${selectedLayer.property.min === 10 ? "st Decile" : ""})`}
          fill={selectedLayer.fill}
          toggle={isLegCollapsed}
        />
      )}
      {selectedLayer?.type === "KewPointLayer" && (
        <GenericLegend
          min={`${selectedLayer.min ?? "Least"}`}
          max={`${selectedLayer.max ?? "Most"}`}
          fill={selectedLayer.fill}
          toggle={isLegCollapsed}
        />
      )}
      {selectedLayer?.type === "WFSLayer" && (
        <GenericLegend
          min={natmap_outputs[selectedLayer.propIdx].min}
          max={natmap_outputs[selectedLayer.propIdx].max}
          fill={selectedLayer.fill}
          toggle={isLegCollapsed}
        />
      )}
      {selectedLayer?.type === "KewShapeLayer" && selectedLayer.metric.name !== "none" && (
        <GenericLegend
          min={`${selectedLayer.min?.toFixed(4) ?? "Least"}`}
          max={`${selectedLayer.max?.toFixed(4) ?? "Most"}`}
          fill={selectedLayer.fill}
          toggle={isLegCollapsed}
        />
      )}
      {selectedLayer?.type === "PaintbrushGeoJSONLayer" && (
        <PaintbrushGeoJSONLegend layer={selectedLayer} toggle={isLegCollapsed} mutate={data => mutate(data)} />
      )}
      <LayerSettingsHeader
        collapsed={settingsCollapsed}
        onToggle={() => setSettingsCollapsed(collapsed => !collapsed)}
        valueLabel={selectedLayer !== null ? `opacity:${Math.round(selectedLayer.opacity * 100)}%` : undefined}
      />
      {!settingsCollapsed && (
      <div id="layer-settings-body" className="p-3 bg-white text-nowrap" style={{ maxHeight: "50vh", overflowY: "auto" }}>
        {selectedLayer !== null ? (
          <>
            {lockLayerName ? (
              <div className="form-control-plaintext border rounded px-2 py-1 small text-muted">{selectedLayer.name}</div>
            ) : (
              <input
                type="text"
                className="form-control"
                placeholder="Layer name"
                value={selectedLayer.name}
                title={selectedLayer.name}
                onChange={event => mutate({ name: event.target.value })}
              />
            )}
            <div className="d-flex align-items-center mt-3">
              Opacity
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                className="custom-range ml-3"
                value={selectedLayer.opacity}
                onChange={event => mutate({ opacity: Number(event.target.value) })}
              />
            </div>
            {styleCopyTargetId !== undefined && (() => {
              if (!isModelDatasetLayer(selectedLayer)) {
                return null
              }
              const layerStats = getLayerData(selectedLayer)
              if (!isTileGridLoaded(layerStats)) {
                return null
              }
              return (
                <CopyLayerStyleControl
                  targetLayerId={styleCopyTargetId}
                  targetGridType={layerStats.type}
                  entries={modelDatasetStyleEntries}
                  onApply={(style, source) => {
                    if (!isModelDatasetLayer(selectedLayer) || source === undefined) {
                      mutate(style as Record<string, unknown>)
                      return
                    }
                    if (layerStats.type !== "CategoricalTileGrid") {
                      mutate(style as Record<string, unknown>)
                      return
                    }
                    const sourceStats = getLayerData(source.layer)
                    if (sourceStats.type !== "CategoricalTileGrid") {
                      mutate(style as Record<string, unknown>)
                      return
                    }
                    mutate(
                      mergeCategoricalStyleOnCopy(
                        style,
                        source.layer,
                        labelEntriesFromStatsLabels(sourceStats.labels),
                        labelEntriesFromStatsLabels(layerStats.labels),
                        layerStats.max
                      ) as Record<string, unknown>
                    )
                  }}
                />
              )
            })()}
            {selectedLayer.type === "OverlayLayer" && (
              <OverlayLayerSettings layer={selectedLayer} mutate={data => mutate(data)} />
            )}
            {selectedLayer.type === "NevoLayer" && (
              <NevoLayerSettings layer={selectedLayer} mutate={data => mutate(data)} />
            )}
            {(selectedLayer.type === "ModelOutputLayer" || selectedLayer.type === "DatasetLayer") && (
              <ModelOutputLayerSettings
                layer={selectedLayer}
                mutate={data => mutate(data)}
                layerType={getLayerData(selectedLayer).type}
              />
            )}
            {showLoadInClassesButton && (
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={onLoadInClasses}
                >
                  Load in classes
                </button>
              </div>
            )}
            {selectedLayer.type === "CehLandCoverLayer" && <CehLandCoverLayerSettings />}
            {selectedLayer.type === "CropMapLayer" && <CropMapLayerSettings layer={selectedLayer} />}
            {selectedLayer.type === "AtiLayer" && <ATILayerSettings layer={selectedLayer} />}
            {selectedLayer.type === "GeoserverLayer" && (
              <GeoserverLayerSettings layer={selectedLayer} mutate={data => mutate(data)} />
            )}
            {selectedLayer.type === "ShapeLayer" && (
              <ShapeLayerSettings layer={selectedLayer} mutate={data => mutate(data)} />
            )}
            {selectedLayer.type === "KewLayer" && (
              <KewLayerSettings layer={selectedLayer} mutate={data => mutate(data)} />
            )}
            {selectedLayer.type === "KewPointLayer" && (
              <KewPointLayerSettings layer={selectedLayer} mutate={data => mutate(data)} />
            )}
            {selectedLayer.type === "KewShapeLayer" && (
              <KewShapeLayerSettings layer={selectedLayer} mutate={data => mutate(data)} />
            )}
            {selectedLayer.type === "ORValLayer" && (
              <>
                <img
                  src={
                    selectedLayer.style !== ""
                      ? `https://landscapes.wearepal.ai/geoserver/wms?REQUEST=GetLegendGraphic&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&LAYER=${selectedLayer.source}&STYLE=${selectedLayer.style}`
                      : ""
                  }
                  alt=""
                />
              </>
            )}
            {selectedLayer.type === "IMDLayer" && (
              <IMDLayerSettings layer={selectedLayer} mutate={data => mutate(data)} />
            )}
            {selectedLayer.type === "WFSLayer" && (
              <WFSLayerSettings layer={selectedLayer} mutate={data => mutate(data)} />
            )}
          </>
        ) : (
          <em>No layer selected</em>
        )}
      </div>
      )}
    </>
  )
}
